import { useRef, useState, useEffect } from "react";
import axios from "axios";
import { CheckCircle2, User, Users, HeartPulse, Target, FileCheck, PenLine, ChevronDown, ChevronRight, Loader2, Trash2, ClipboardList, FileDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VERTRAG_CHECKLIST = [
  { key: "leistungen", label: "Leistungen und Grenzen erklärt" },
  { key: "kosten",     label: "Kosten und Wahlleistungen erklärt" },
  { key: "besuch",     label: "Besuchszeiten erklärt" },
  { key: "notfall",    label: "Notfallmanagement erklärt" },
  { key: "datenschutz",label: "Datenschutz / Schweigepflicht erklärt" },
  { key: "vertrag",    label: "Vertrag vorhanden" },
  { key: "schluessel", label: "Schlüsselübergabe vereinbart" },
  { key: "kv",         label: "Kostenvoranschlag vorhanden" },
];

const ROLLE_OPTIONS = ["bitte auswählen", "Klient/in selbst", "Angehörige:r", "Betreuer:in", "Bevollmächtigte:r", "Sonstige"];

function Section({ nr, title, Icon, children, open, onToggle }) {
  return (
    <div className="rounded-2xl bg-white border border-cream-300 overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 p-4 sm:p-5 text-left hover:bg-cream-50 transition-colors"
        aria-expanded={open}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className="h-9 w-9 rounded-xl bg-sand-800 text-cream-50 inline-flex items-center justify-center shrink-0 font-bold text-[13px]">
            {String(nr).padStart(2, "0")}
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-sand-800 font-semibold text-[15px] sm:text-[16px]">
              <Icon className="h-4 w-4 text-brand-darkred" strokeWidth={1.9} />
              {title}
            </div>
          </div>
        </div>
        {open ? <ChevronDown className="h-4 w-4 text-sand-600 shrink-0" strokeWidth={2} />
              : <ChevronRight className="h-4 w-4 text-sand-600 shrink-0" strokeWidth={2} />}
      </button>
      {open && <div className="px-4 sm:px-5 pb-5 pt-1 space-y-3">{children}</div>}
    </div>
  );
}

const Field = ({ id, label, children }) => (
  <label htmlFor={id} className="block">
    <span className="text-[12px] uppercase tracking-[0.14em] font-semibold text-sand-700">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

const Txt = (p) => (
  <input {...p} className="w-full h-11 px-3 rounded-xl bg-white border border-cream-300 focus:border-brand-darkred focus:outline-none text-[15px]" />
);
const TxtArea = (p) => (
  <textarea {...p} className="w-full px-3 py-2.5 rounded-xl bg-white border border-cream-300 focus:border-brand-darkred focus:outline-none text-[15px] leading-relaxed resize-none" />
);

// Signature Canvas (Finger/Stift/Maus)
function SignaturePad({ onChange }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const resize = () => {
      const rect = c.getBoundingClientRect();
      c.width = rect.width * window.devicePixelRatio;
      c.height = 180 * window.devicePixelRatio;
      const ctx = c.getContext("2d");
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
      ctx.strokeStyle = "#3a2b21";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const pos = (e) => {
    const c = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - c.left, y: t.clientY - c.top };
  };
  const start = (e) => {
    e.preventDefault();
    setDrawing(true);
    const p = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };
  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const p = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    if (!hasInk) setHasInk(true);
  };
  const end = () => {
    setDrawing(false);
    if (hasInk && onChange) onChange(canvasRef.current.toDataURL("image/png"));
  };
  const clear = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setHasInk(false);
    if (onChange) onChange(null);
  };

  return (
    <div>
      <div className="rounded-xl bg-white border-2 border-dashed border-cream-300 relative overflow-hidden" style={{ height: 180 }}>
        <canvas
          ref={canvasRef}
          data-testid="pki-aufnahme-signature-canvas"
          className="w-full h-full touch-none block cursor-crosshair"
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        />
        {!hasInk && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-sand-500 text-[13.5px]">
            Hier mit Finger, Stift oder Maus unterschreiben
          </div>
        )}
      </div>
      <div className="mt-2 flex items-center gap-3 text-[12.5px]">
        <button type="button" onClick={clear} data-testid="pki-aufnahme-signature-clear"
          className="inline-flex items-center gap-1.5 rounded-full bg-sand-800 text-cream-50 h-9 px-4 font-semibold hover:opacity-90">
          <Trash2 className="h-3.5 w-3.5" strokeWidth={2} /> Unterschrift löschen
        </button>
        <span className={`font-medium ${hasInk ? "text-emerald-700" : "text-sand-500"}`}>
          {hasInk ? "Unterschrift erfasst" : "Noch keine Unterschrift"}
        </span>
      </div>
    </div>
  );
}

export default function PkiAufnahmegespraech() {
  const [open, setOpen] = useState(1);
  // 6 Sektionen State
  const [klient, setKlient] = useState({ name: "", geb: "", adresse: "", pflegegrad: "", diagnosen: "" });
  const [angeh, setAngeh] = useState({ name: "", telefon: "", beziehung: "", vollmacht: "" });
  const [ges, setGes] = useState({ mobilitaet: "", ernaehrung: "", kognition: "", risiken: "", medikamente: "" });
  const [wunsch, setWunsch] = useState({ ziele: "", tagesstruktur: "", besonderheiten: "" });
  const [checks, setChecks] = useState({});
  const [best, setBest] = useState({ name: "", rolle: "bitte auswählen", bestaetigt: false, signature: null });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dokukette, setDokukette] = useState(null);

  const toggleCheck = (k) => setChecks((c) => ({ ...c, [k]: !c[k] }));

  const canSubmit = () =>
    klient.name.trim().length > 1 &&
    (ges.mobilitaet + ges.ernaehrung + ges.kognition + ges.risiken).trim().length > 8 &&
    best.name.trim().length > 1 &&
    best.rolle !== "bitte auswählen" &&
    best.bestaetigt &&
    best.signature;

  const buildBeobachtungText = () => {
    const lines = [
      `Aufnahmegespräch – ${klient.name}${klient.geb ? " (geb. " + klient.geb + ")" : ""}`,
      klient.adresse && `Adresse: ${klient.adresse}`,
      klient.pflegegrad && `Pflegegrad: ${klient.pflegegrad}`,
      klient.diagnosen && `Diagnosen: ${klient.diagnosen}`,
      "",
      "Bezugsperson: " + [angeh.name, angeh.beziehung, angeh.telefon].filter(Boolean).join(" · "),
      angeh.vollmacht && `Vollmacht/Betreuung: ${angeh.vollmacht}`,
      "",
      "Gesundheit & Risiken:",
      ges.mobilitaet && `- Mobilität: ${ges.mobilitaet}`,
      ges.ernaehrung && `- Ernährung: ${ges.ernaehrung}`,
      ges.kognition && `- Kognition/Verhalten: ${ges.kognition}`,
      ges.risiken && `- Risiken: ${ges.risiken}`,
      ges.medikamente && `- Medikamente: ${ges.medikamente}`,
      "",
      "Wünsche & Ziele:",
      wunsch.ziele && `- Ziele: ${wunsch.ziele}`,
      wunsch.tagesstruktur && `- Tagesstruktur: ${wunsch.tagesstruktur}`,
      wunsch.besonderheiten && `- Besonderheiten: ${wunsch.besonderheiten}`,
      "",
      "Vertrags- und Aufnahmepunkte: " +
        (VERTRAG_CHECKLIST.filter((c) => checks[c.key]).length
          ? VERTRAG_CHECKLIST.filter((c) => checks[c.key]).map((c) => c.label).join(" · ")
          : "(keine Häkchen)"),
      "",
      `Bestätigt am Aufnahmetag durch ${best.name} (${best.rolle}).`,
    ].filter(Boolean).join("\n");
    return lines;
  };

  const submit = async () => {
    setError(null);
    if (!canSubmit()) {
      setError("Bitte alle Pflichtfelder ausfüllen: Name des Klienten, Gesundheit/Risiken, Bestätigung + Unterschrift.");
      setOpen(6);
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/pflege-ki/dokukette`, {
        beobachtung: buildBeobachtungText(),
        kunde: klient.name,
      }, { withCredentials: true });
      setDokukette(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Konnte die Aufnahme-Dokukette nicht erstellen. Bitte gleich noch mal versuchen.");
    } finally {
      setLoading(false);
    }
  };

  if (dokukette) {
    const STEPS = [
      ["beobachtung", "Beobachtung"], ["risiko", "Risiko"], ["massnahme", "Maßnahme"],
      ["kommunikation", "Kommunikation"], ["evaluation", "Evaluation"],
    ];
    const downloadPdf = async () => {
      try {
        const r = await axios.post(`${API}/pflege-ki/aufnahme/pdf`, {
          kunde: klient.name,
          beobachtung: buildBeobachtungText(),
          dokukette,
          unterzeichner: best.name,
          unterzeichner_rolle: best.rolle,
          signature_data_url: best.signature,
        }, { withCredentials: true, responseType: "blob" });
        const url = URL.createObjectURL(r.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = `aufnahme-dokukette-${klient.name.toLowerCase().replace(/[^a-z0-9]/g, "-") || "bewohner"}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      } catch (e) { setError("PDF-Export fehlgeschlagen. Bitte erneut versuchen."); }
    };
    // Speichere Aufnahme in localStorage-Klientenakte
    try {
      const store = JSON.parse(localStorage.getItem("dh-klienten-akte") || "{}");
      const key = klient.name.trim() || "Unbenannt";
      const arr = store[key] || [];
      arr.unshift({
        typ: "aufnahme", ts: new Date().toISOString(),
        klient, angeh, ges, wunsch, checks, best: { ...best, signature: null },
        dokukette,
      });
      store[key] = arr.slice(0, 20);
      localStorage.setItem("dh-klienten-akte", JSON.stringify(store));
    } catch { /* noop */ }
    return (
      <div data-testid="pki-aufnahme-result" className="space-y-3">
        <div className="text-[12px] uppercase tracking-[0.22em] font-semibold text-brand-darkred">
          Aufnahme-Dokukette · {klient.name}
        </div>
        <ol className="space-y-3">
          {STEPS.map(([key, label], i) => (
            <li key={key} className="rounded-2xl bg-white border border-cream-300 p-4 sm:p-5">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-sand-800 text-cream-50 inline-flex items-center justify-center shrink-0 font-bold text-[13px]">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sand-800 text-[15px]">{label}</div>
                  <p className="mt-2 text-[14px] text-sand-700 leading-relaxed">{dokukette[key] || "—"}</p>
                </div>
              </div>
            </li>
          ))}
        </ol>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button type="button" onClick={downloadPdf}
            data-testid="pki-aufnahme-pdf"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-darkred hover:bg-brand-darkred/90 text-white h-12 text-[14.5px] font-semibold">
            <FileDown className="h-4 w-4" strokeWidth={2} /> Als PDF speichern
          </button>
          <button type="button" onClick={() => {
              setDokukette(null);
              setKlient({ name: "", geb: "", adresse: "", pflegegrad: "", diagnosen: "" });
              setAngeh({ name: "", telefon: "", beziehung: "", vollmacht: "" });
              setGes({ mobilitaet: "", ernaehrung: "", kognition: "", risiken: "", medikamente: "" });
              setWunsch({ ziele: "", tagesstruktur: "", besonderheiten: "" });
              setChecks({});
              setBest({ name: "", rolle: "bitte auswählen", bestaetigt: false, signature: null });
              setOpen(1);
            }}
            data-testid="pki-aufnahme-reset"
            className="w-full inline-flex items-center justify-center rounded-full bg-white border border-cream-300 hover:border-brand-darkred/40 text-sand-800 h-12 text-[14.5px] font-semibold">
            Neue Aufnahme starten
          </button>
        </div>
        {error && (
          <div className="rounded-xl border border-brand-darkred/40 bg-peach-50 p-3 text-[13px] text-brand-darkred">
            {error}
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="pki-aufnahme" className="space-y-4">
      <div>
        <h2 className="font-display text-2xl text-sand-800 leading-tight">Aufnahmegespräch</h2>
        <p className="text-[13.5px] text-sand-600 mt-1 leading-relaxed">
          Strukturiertes Aufnahmeformular in 6 Sektionen. Am Ende erstellt die
          KI daraus die komplette Aufnahme-Dokukette.
        </p>
      </div>

      <Section nr={1} title="Klient:in / Bewohner:in" Icon={User} open={open === 1} onToggle={() => setOpen(open === 1 ? 0 : 1)}>
        <Field id="k-name" label="Vor- und Nachname *">
          <Txt id="k-name" data-testid="pki-aufnahme-klient-name" value={klient.name} onChange={(e) => setKlient({ ...klient, name: e.target.value })} placeholder="z. B. Herr Peter Müller" />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field id="k-geb" label="Geburtsdatum">
            <Txt id="k-geb" value={klient.geb} onChange={(e) => setKlient({ ...klient, geb: e.target.value })} placeholder="TT.MM.JJJJ" />
          </Field>
          <Field id="k-pg" label="Pflegegrad">
            <Txt id="k-pg" value={klient.pflegegrad} onChange={(e) => setKlient({ ...klient, pflegegrad: e.target.value })} placeholder="z. B. PG 3" />
          </Field>
        </div>
        <Field id="k-adr" label="Adresse">
          <Txt id="k-adr" value={klient.adresse} onChange={(e) => setKlient({ ...klient, adresse: e.target.value })} placeholder="Straße, PLZ, Ort" />
        </Field>
        <Field id="k-dx" label="Diagnosen (Hauptdiagnosen)">
          <TxtArea id="k-dx" rows={2} value={klient.diagnosen} onChange={(e) => setKlient({ ...klient, diagnosen: e.target.value })} placeholder="z. B. Vaskuläre Demenz, Diabetes mellitus Typ 2, Hypertonie" />
        </Field>
      </Section>

      <Section nr={2} title="Angehörige / Bezugsperson" Icon={Users} open={open === 2} onToggle={() => setOpen(open === 2 ? 0 : 2)}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field id="a-name" label="Name">
            <Txt id="a-name" value={angeh.name} onChange={(e) => setAngeh({ ...angeh, name: e.target.value })} placeholder="Vor- und Nachname" />
          </Field>
          <Field id="a-tel" label="Telefon">
            <Txt id="a-tel" value={angeh.telefon} onChange={(e) => setAngeh({ ...angeh, telefon: e.target.value })} placeholder="0170..." />
          </Field>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field id="a-bez" label="Beziehung">
            <Txt id="a-bez" value={angeh.beziehung} onChange={(e) => setAngeh({ ...angeh, beziehung: e.target.value })} placeholder="Tochter / Sohn / Ehepartner:in" />
          </Field>
          <Field id="a-vm" label="Vollmacht / gesetzliche Betreuung">
            <Txt id="a-vm" value={angeh.vollmacht} onChange={(e) => setAngeh({ ...angeh, vollmacht: e.target.value })} placeholder="Vorsorgevollmacht / rechtl. Betreuung" />
          </Field>
        </div>
      </Section>

      <Section nr={3} title="Gesundheit & Risiken *" Icon={HeartPulse} open={open === 3} onToggle={() => setOpen(open === 3 ? 0 : 3)}>
        <Field id="g-mob" label="Mobilität">
          <TxtArea id="g-mob" rows={2} data-testid="pki-aufnahme-mobilitaet" value={ges.mobilitaet} onChange={(e) => setGes({ ...ges, mobilitaet: e.target.value })} placeholder="z. B. Rollator, benötigt Hilfe beim Transfer, Sturzrisiko erhöht" />
        </Field>
        <Field id="g-ern" label="Ernährung / Trinken">
          <TxtArea id="g-ern" rows={2} value={ges.ernaehrung} onChange={(e) => setGes({ ...ges, ernaehrung: e.target.value })} placeholder="z. B. weiche Kost, wenig Trinken, MNA-Score" />
        </Field>
        <Field id="g-kog" label="Kognition / Verhalten">
          <TxtArea id="g-kog" rows={2} value={ges.kognition} onChange={(e) => setGes({ ...ges, kognition: e.target.value })} placeholder="z. B. Orientierung reduziert, tagsüber ruhig, nachts unruhig" />
        </Field>
        <Field id="g-ris" label="Weitere Risiken (Dekubitus, Dehydration, Sturz…)">
          <TxtArea id="g-ris" rows={2} value={ges.risiken} onChange={(e) => setGes({ ...ges, risiken: e.target.value })} placeholder="z. B. Dekubitus-Risiko Kat. 2, erhöhtes Sturzrisiko" />
        </Field>
        <Field id="g-med" label="Medikamente (Kurzliste)">
          <TxtArea id="g-med" rows={2} value={ges.medikamente} onChange={(e) => setGes({ ...ges, medikamente: e.target.value })} placeholder="Medikationsplan vorhanden" />
        </Field>
      </Section>

      <Section nr={4} title="Wünsche & Ziele" Icon={Target} open={open === 4} onToggle={() => setOpen(open === 4 ? 0 : 4)}>
        <Field id="w-ziele" label="Individuelle Ziele der Versorgung">
          <TxtArea id="w-ziele" rows={2} value={wunsch.ziele} onChange={(e) => setWunsch({ ...wunsch, ziele: e.target.value })} placeholder="z. B. Selbständigkeit erhalten, so lange wie möglich zu Hause bleiben" />
        </Field>
        <Field id="w-tag" label="Tagesstruktur / Vorlieben">
          <TxtArea id="w-tag" rows={2} value={wunsch.tagesstruktur} onChange={(e) => setWunsch({ ...wunsch, tagesstruktur: e.target.value })} placeholder="Aufsteh-/Schlafenszeiten, Mahlzeiten, Rituale" />
        </Field>
        <Field id="w-bes" label="Besonderheiten / Wichtiges">
          <TxtArea id="w-bes" rows={2} value={wunsch.besonderheiten} onChange={(e) => setWunsch({ ...wunsch, besonderheiten: e.target.value })} placeholder="Religion, Sprache, Angst-Themen, Haustier" />
        </Field>
      </Section>

      <Section nr={5} title="Vertrags- und Aufnahmepunkte" Icon={FileCheck} open={open === 5} onToggle={() => setOpen(open === 5 ? 0 : 5)}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {VERTRAG_CHECKLIST.map((c) => (
            <label key={c.key} className="flex items-start gap-2.5 py-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={!!checks[c.key]}
                onChange={() => toggleCheck(c.key)}
                data-testid={`pki-aufnahme-check-${c.key}`}
                className="mt-1 h-4 w-4 rounded border-cream-400 accent-brand-darkred"
              />
              <span className="text-[14px] text-sand-800 leading-snug">{c.label}</span>
            </label>
          ))}
        </div>
      </Section>

      <Section nr={6} title="Bestätigung & Unterschrift *" Icon={PenLine} open={open === 6} onToggle={() => setOpen(open === 6 ? 0 : 6)}>
        <div className="rounded-xl bg-cream-50 border border-cream-300 p-3.5 text-[13px] leading-relaxed text-sand-700">
          Mit der Unterschrift bestätigt die unterzeichnende Person, dass die
          Angaben gemeinsam besprochen wurden und die Aufnahme in der oben
          dokumentierten Form bestätigt wird.
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field id="b-name" label="Name der unterzeichnenden Person *">
            <Txt id="b-name" data-testid="pki-aufnahme-best-name" value={best.name} onChange={(e) => setBest({ ...best, name: e.target.value })} placeholder="Vor- und Nachname" />
          </Field>
          <Field id="b-rolle" label="Unterschrift als *">
            <select id="b-rolle" data-testid="pki-aufnahme-best-rolle"
              value={best.rolle} onChange={(e) => setBest({ ...best, rolle: e.target.value })}
              className="w-full h-11 px-3 rounded-xl bg-white border border-cream-300 focus:border-brand-darkred focus:outline-none text-[15px]">
              {ROLLE_OPTIONS.map((r) => <option key={r}>{r}</option>)}
            </select>
          </Field>
        </div>
        <label className="flex items-start gap-2.5 rounded-xl bg-cream-50 border border-cream-300 p-3.5 cursor-pointer">
          <input
            type="checkbox" checked={best.bestaetigt}
            onChange={(e) => setBest({ ...best, bestaetigt: e.target.checked })}
            data-testid="pki-aufnahme-best-check"
            className="mt-1 h-4 w-4 rounded border-cream-400 accent-brand-darkred"
          />
          <span className="text-[14px] text-sand-800 leading-snug">
            Ich bestätige, dass die Aufnahme besprochen wurde und die dokumentierten Angaben nach bestem Wissen richtig sind.
          </span>
        </label>
        <SignaturePad onChange={(sig) => setBest((b) => ({ ...b, signature: sig }))} />
      </Section>

      {error && (
        <div data-testid="pki-aufnahme-error" className="rounded-xl border border-brand-darkred/40 bg-peach-50 p-3 text-[13px] text-brand-darkred">
          {error}
        </div>
      )}

      <button
        type="button" onClick={submit} disabled={loading}
        data-testid="pki-aufnahme-submit"
        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-darkred hover:bg-brand-darkred/90 text-white h-14 text-[15.5px] font-semibold transition-colors disabled:opacity-70"
      >
        {loading ? <><Loader2 className="h-5 w-5 animate-spin" strokeWidth={2} /> Dokukette wird gebaut…</> :
                   <><ClipboardList className="h-5 w-5" strokeWidth={1.9} /> Komplette Aufnahme-Dokukette erstellen</>}
      </button>

      <div className="text-[12px] text-sand-500 text-center flex items-center justify-center gap-1.5">
        <CheckCircle2 className="h-3 w-3" strokeWidth={2} />
        Alle Angaben bleiben lokal im Browser · Freigabe durch Pflegefachkraft.
      </div>
    </div>
  );
}
